import { cloneDeep } from 'lodash';
import { DateRangeValue, DynamicRange, FixedDateRange, Model, Query, Schema } from '@hub-components';
import { Table } from './table.model';

export type DateRangeValueJson = { type: 'fixed', start_date: string, end_date: string } | { type: 'dynamic', dynamic: DynamicRange };
export type LegacyDateRangeValueJson = { type: 'dynamic' | 'fixed', value: string };

export type Settings<DateRangeValueType> = {
  interval?: DateRangeValueType,
};

export interface ReportJson {
  account: string;
  name: string
  public_uuid: string
  settings: Settings<DateRangeValueJson | LegacyDateRangeValueJson>
  tables: any[]
  is_template: boolean;
}

export class Report extends Model {
  public name: string;
  public owner: string;
  public account: string;
  public editors: string[];
  public tables: Table[];
  public settings: Settings<DateRangeValue> & { legacyInterval?: LegacyDateRangeValueJson };
  public shareUuid: string;
  public publicUuid: string;
  public permissions: any;
  public isTemplate: boolean;
  public hasEditorPermission: boolean;
  public reportId: number;

  constructor(obj: Partial<ReportJson> = {}, schemas: Schema[] = []) {
    super(obj);
    if (obj.settings) {
      if (obj.settings.interval) {
        const interval = this.parseDateRangeValue(obj.settings.interval);
        if (interval) {
          this.settings.interval = interval;
        } else {
          this.settings.legacyInterval = <LegacyDateRangeValueJson> obj.settings.interval;
          delete this.settings.interval;
        }
      }
    } else {
      this.settings = {};
    }
    let tableNameOccurrences = {};
    this.tables = (this.tables || []).map((t, i) => {
      const schema = schemas[i];
      const query = new Query(t['query'], schema);
      query.auth = t.query.auth;
      const options = t.options;
      if (options?.dateOptions?.interval) {
        options.dateOptions.interval = this.parseDateRangeValue(options.dateOptions.interval as (DateRangeValueJson | LegacyDateRangeValueJson));
      }

      let tableName = t['name'] && t['name'].substring(0, 31);
      tableNameOccurrences[tableName] = (tableNameOccurrences[tableName] || 0) + 1;
      if (tableNameOccurrences[tableName] > 1) {
        return new Table(
          (`${tableName.substring(0, 29 - (tableNameOccurrences[tableName] - 1).toString().length)}(${tableNameOccurrences[tableName] - 1})`),
          query,
          options,
        );
      } else {
        return new Table(tableName, query, options);
      }
    });
  }

  public addTable() {
    const currentTableNamesNumber = this.tables.filter(table => table.name.includes('Table ')).map(table => parseInt(table.name.replace('Table ', '')));
    const tableNumber = currentTableNamesNumber.length ? Math.max(...currentTableNamesNumber) + 1 : 1;
    const name = `Table ${tableNumber}`;

    this.tables.push(
      new Table(
        name,
        new Query({ interval: this.tables[0] && this.tables[0].query.interval }),
      ),
    );
  }

  public duplicateTable(index: number) {
    const tableToDuplicate = this.tables[index];
    const baseName = tableToDuplicate.name.replace(/\(\d+\)$/, '');
    let highestNumber = 0;

    this.tables.forEach((table) => {
      const match = table.name.match(new RegExp(`^${baseName}\\((\\d+)\\)$`));
      if (match) {
        const number = parseInt(match[1], 10);
        if (number > highestNumber) {
          highestNumber = number;
        }
      }
    });
    const newNumber = highestNumber + 1;
    const newName = `${baseName}(${newNumber})`;

    const duplicatedTable = new Table(
      newName,
      tableToDuplicate.query.clone(),
      cloneDeep(tableToDuplicate.options),
    );
    this.tables.splice(index + 1, 0, duplicatedTable);
  }

  public getCompactedDatasets() {
    let compactedDatasets = new Set();
    for (let t of this.tables) {
      if (t.query.wasCompacted) {
        compactedDatasets.add(t.query.schema.displayName);
      }
    }
    return compactedDatasets;
  }

  public isValid() {
    return this.tables.map('query').every((q: Query) => q.isValid());
  }

  public asJson(): ReportJson {
    return {
      account: this.account,
      name: this.name,
      public_uuid: this.publicUuid,
      settings: {
        ...this.settings,
        interval: this.jsonDateRangeValue(this.settings.interval),
      },
      tables: this.tables.map(t => {
        if (!!t.options?.dateOptions?.interval) {
          t.options.dateOptions.interval = this.jsonDateRangeValue(t.options.dateOptions.interval) as any;
        }
        return t.asJson();
      }),
      is_template: this.isTemplate,
    };
  }

  private parseDateRangeValue(range: DateRangeValueJson | LegacyDateRangeValueJson): DateRangeValue {
    if ('dynamic' in range) {
      return {
        type: 'dynamic',
        dynamic: range['dynamic'],
      };
    } else if (range.type === 'dynamic') {
      const match = (range['value'] || '').match(/^(this|last|yesterday)(?:(?:_(\d+))?_(day|week|month|year)s?)?$/);
      if (match) {
        return {
          type: 'dynamic',
          dynamic: { name: match[1], num: match[2] && +match[2], unit: <any> match[3] },
        };
      }
    } else if ('start_date' in range) {
      const startDate = range.start_date;
      const endDate = range.end_date;
      return {
        type: 'fixed',
        startDate: Date.create(startDate),
        endDate: Date.create(endDate),
      };
    } else {
      const [startDate, endDate] = range['value'].split('-', 2);
      return {
        type: 'fixed',
        startDate: Date.create(startDate),
        endDate: Date.create(endDate),
      };
    }
  }

  private jsonDateRangeValue(range: DateRangeValue): DateRangeValueJson & LegacyDateRangeValueJson {
    if ('dynamic' in range && range['dynamic']) {
      const { name, num, unit } = range['dynamic'];
      let value = name;
      if (num && num > 1) {
        value += `_${num}`;
      }
      if (unit) {
        value += `_${unit}`;
        if (num && num > 1) {
          value += 's';
        }
      }
      return {
        type: 'dynamic',
        value: value,
        dynamic: range['dynamic'],
      };
    } else {
      const { startDate, endDate } = <FixedDateRange> range;
      const startDateStr = startDate.format('{yyyy}-{MM}-{dd}');
      const endDateStr = endDate.format('{yyyy}-{MM}-{dd}');
      return {
        type: 'fixed',
        value: `${startDateStr.removeAll('-')}-${endDateStr.removeAll('-')}`,
        start_date: startDateStr,
        end_date: endDateStr,
      };
    }
  }
}
